import { MapPin, Phone, ExternalLink } from "lucide-react";
import {
  BUSINESS_NAME,
  GOOGLE_BUSINESS_PROFILE_URL,
  GOOGLE_MAPS_EMBED_SRC,
  PHONE_DISPLAY,
  PHONE_HREF,
  SERVICE_AREA_TEXT_EXTENDED,
} from "@/data/business";

interface ServiceAreaMapProps {
  /** Optional heading override; defaults to the local-SEO wording. */
  heading?: string;
  className?: string;
}

/**
 * "Serving the Greater Nashville Area" block — map embed + Google Business
 * Profile link. The embed and profile link both read from src/data/business.ts,
 * so swapping in the verified GBP place ID needs no changes here.
 */
const ServiceAreaMap = ({ heading = "Serving the Greater Nashville Area", className = "" }: ServiceAreaMapProps) => (
  <section className={`bg-accent/30 ${className}`} aria-labelledby="service-area-map-heading">
    <div className="container py-14 md:py-16">
      <div className="max-w-4xl mx-auto text-center mb-8">
        <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-warm/15 mb-5">
          <MapPin className="h-5 w-5 text-warm" aria-hidden />
        </div>
        <h2 id="service-area-map-heading" className="text-2xl md:text-3xl font-heading font-semibold mb-4">
          {heading}
        </h2>
        <div className="w-12 h-px bg-warm mx-auto mb-5" />
        <p className="text-foreground/80 leading-relaxed">
          {BUSINESS_NAME} provides non-medical companion care in {SERVICE_AREA_TEXT_EXTENDED}. We come to you — there is
          no office to visit.
        </p>
      </div>

      <div className="rounded-xl overflow-hidden border border-border max-w-4xl mx-auto aspect-video bg-muted">
        <iframe
          title={`${BUSINESS_NAME} — greater Nashville, TN service area map`}
          src={GOOGLE_MAPS_EMBED_SRC}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      <div className="max-w-4xl mx-auto mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
        {GOOGLE_BUSINESS_PROFILE_URL ? (
          <a
            href={GOOGLE_BUSINESS_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center gap-2 font-medium text-primary underline underline-offset-4"
          >
            Find us on Google <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
        ) : (
          <p className="text-muted-foreground">
            Find us on Google — our Google Business Profile listing is being verified.
          </p>
        )}
        <a
          href={PHONE_HREF}
          className="inline-flex min-h-[44px] items-center gap-2 font-medium text-primary underline underline-offset-4"
        >
          <Phone className="h-4 w-4" aria-hidden /> Call {PHONE_DISPLAY}
        </a>
      </div>
    </div>
  </section>
);

export default ServiceAreaMap;
